/* ==========================================================================
   TechNest — facets.js
   Progressive enhancement over Shopify's native storefront filtering.

   The form already works without JavaScript. This file intercepts changes,
   fetches the same URL through the Section Rendering API and swaps in the new
   grid — while pushing the identical URL into history, so refresh, share and
   back all behave exactly as they would without JS.
   ========================================================================== */
(function () {
  'use strict';

  const TN = window.TechNest || {};
  const utils = TN.utils;
  if (!utils) return;

  class TnFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.grid = document.querySelector('[data-tn-facet-results]');

      // Checkbox and price changes are debounced so rapid clicks make one call.
      this._onChange = utils.debounce((event) => {
        const form = event.target.closest('[data-tn-facet-form]');
        if (!form) return;
        this.apply(form);
      }, 350);

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-tn-sort]')) {
          // Sorting should feel instant.
          const form = event.target.closest('[data-tn-facet-form]');
          if (form) this.apply(form);
          return;
        }
        this._onChange(event);
      });

      this.addEventListener('click', (event) => {
        const clear = event.target.closest('[data-tn-facet-clear], [data-tn-facet-remove]');
        if (!clear) return;
        event.preventDefault();
        this.navigate(clear.getAttribute('href'));
      });

      // Back/forward must restore the matching results.
      this._onPopState = () => this.navigate(window.location.href, false);
      window.addEventListener('popstate', this._onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this._onPopState);
    }

    /** Build the URL from the form exactly as a native submit would. */
    apply(form) {
      const params = new URLSearchParams(new FormData(form));
      // Drop empty price bounds so they don't appear as bare params.
      Array.from(params.keys()).forEach((key) => {
        if (params.get(key) === '') params.delete(key);
      });
      const query = params.toString();
      const url = window.location.pathname + (query ? '?' + query : '');
      this.navigate(url);
    }

    async navigate(url, pushState) {
      if (!url) return;
      this.setLoading(true);

      try {
        const target = new URL(url, window.location.origin);
        const fetchUrl =
          target.pathname +
          target.search +
          (target.search ? '&' : '?') +
          'section_id=' + encodeURIComponent(this.sectionId);

        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error('Facet fetch failed: ' + response.status);
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');

        // Swap the results grid, the toolbar, and both filter forms.
        ['[data-tn-facet-results]', '[data-tn-facet-toolbar]', '[data-tn-facet-active]'].forEach((selector) => {
          const source = parsed.querySelector(selector);
          const dest = document.querySelector(selector);
          if (source && dest) dest.innerHTML = source.innerHTML;
        });

        document.querySelectorAll('[data-tn-facet-panel]').forEach((panel) => {
          const key = panel.getAttribute('data-tn-facet-panel');
          const source = parsed.querySelector('[data-tn-facet-panel="' + key + '"]');
          if (source) panel.innerHTML = source.innerHTML;
        });

        if (pushState !== false) window.history.pushState({ facets: true }, '', url);

        const count = parsed.querySelector('[data-tn-facet-count]');
        if (count) utils.announce(count.textContent.trim());

        document.dispatchEvent(new CustomEvent('tn:section:load', { detail: { target: document } }));

        // Bring the top of the grid into view without yanking the page around.
        const results = document.querySelector('[data-tn-facet-results]');
        if (results && pushState !== false) {
          const top = results.getBoundingClientRect().top + window.scrollY;
          const headerH = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--tn-header-height'),
            10
          ) || 64;
          if (window.scrollY > top - headerH) {
            window.scrollTo({
              top: top - headerH - 16,
              behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth'
            });
          }
        }
      } catch (error) {
        // Fall back to a real page load so filtering never dead-ends.
        window.location.href = url;
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      const results = document.querySelector('[data-tn-facet-results]');
      if (results) {
        results.setAttribute('aria-busy', state ? 'true' : 'false');
        results.classList.toggle('is-loading', !!state);
      }
      document.querySelectorAll('[data-tn-facet-spinner]').forEach((el) => {
        el.hidden = !state;
      });
    }
  }

  utils.define('tn-facets', TnFacets);
})();
